/** @jsxImportSource @emotion/react */

import { useContext } from 'react';
import { CSSObject } from '@emotion/react';
import AttributedText from '../../../../../components/attributedText';
import { IAction } from '../../../../../hooks/useAction';
import RemoteImage, {
  IRemoteImage,
} from '../../../../../components/RemoteImage';
import useTheme from '../../../../../hooks/useTheme';
import { IBlockBaseItem } from '../blockItem';
import Clickable from '../../../../../components/clickable';
import { ScreenContext } from '../../../../../contexts/screenContext';

interface IButtonIcon {
  image: IRemoteImage;
  side: 'start' | 'end';

  size: {
    height: number;
    width: number;
  };
}

interface IButtonDisabledState {
  title: string;
  subtitle?: string;
  titleAlignment?: string;
  color: string;
  icon?: IButtonIcon;
  edgeIcon?: IButtonIcon;
  subtitleIcon?: IButtonIcon;

  enabledByInputIds: Array<string>;
}

export interface IBlockButtonItem extends IBlockBaseItem {
  type: 'button';

  title: string;
  subtitle?: string;
  titleAlignment?: string;
  color: string;
  highlightColor: string;

  icon?: IButtonIcon;
  edgeIcon?: IButtonIcon;
  subtitleIcon?: IButtonIcon;

  disabledState?: IButtonDisabledState;
  iconTitleGutter?: number;
  action?: IAction;
}

interface IBlockButtonItemProps {
  data: IBlockButtonItem;
  dataTestId?: string;
}

const BlockButtonItem = ({
  data,
  dataTestId,
  ...props
}: IBlockButtonItemProps) => {
  const screenContext = useContext(ScreenContext);
  const { resolveColor } = useTheme();

  // The button is disabled if any of the inputs listed in "enabledByInputsIds" are invalid.
  const isDisabled = !!data.disabledState?.enabledByInputIds.find(
    (inputId) => !screenContext.inputStates[inputId]?.isValid
  );

  // Pick the button properties either from the root object or from disabledState,
  // depending on whether the button is enabled or not.
  const {
    title,
    subtitle,
    color,
    icon,
    edgeIcon,
    subtitleIcon,
    titleAlignment,
  } = data.disabledState && isDisabled ? data.disabledState : data;

  const resolvedAlignment =
    titleAlignment === 'start' ? 'flex-start' : 'center';

  const BUTTON_STYLE: CSSObject = {
    borderRadius: data.cornerRadius,
    backgroundColor: resolveColor(color),

    width: 'auto',
    height: 'auto',

    '&[active]': {
      backgroundColor: resolveColor(data.highlightColor),
    },

    justifyContent: resolvedAlignment,
  };

  const ICON_STYLE: CSSObject = {
    width: icon?.size?.width,
    height: icon?.size?.height,
    order: icon?.side === 'end' ? 1 : 0,
    marginLeft: icon?.side === 'end' ? data.iconTitleGutter : 0,
    marginRight: icon?.side === 'start' ? data.iconTitleGutter : 0,
  };

  const EDGE_ICON_STYLE: CSSObject = {
    position: 'absolute',
    ...(edgeIcon?.side === 'end'
      ? { right: data.padding.trailing }
      : { left: data.padding.leading }),
    width: edgeIcon?.size?.width,
    height: edgeIcon?.size?.height,
  };

  const WRAPPER_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
  };

  const SUBTITLE_STYLE: CSSObject = {
    display: 'flex',
    alignItems: 'center',
  };

  const SUBTITLE_ICON_STYLE: CSSObject = {
    width: subtitleIcon?.size?.width,
    height: subtitleIcon?.size?.height,
    marginRight: 4,
  };

  return (
    <Clickable
      styleAs='button'
      action={data.action}
      disabled={isDisabled}
      css={BUTTON_STYLE}
      {...props}
      dataTestId={dataTestId}
    >
      {/* Regular icon */}
      <RemoteImage {...icon?.image} css={ICON_STYLE} />

      {/* Edge icon */}
      <RemoteImage {...edgeIcon?.image} css={EDGE_ICON_STYLE} />

      <span css={WRAPPER_STYLE}>
        {/* Title text */}
        <AttributedText text={title} aria-labelledby={data.accessibility?.id} />

        {/* Subtitle icon and text */}
        <span css={SUBTITLE_STYLE}>
          <RemoteImage {...subtitleIcon?.image} css={SUBTITLE_ICON_STYLE} />
          <AttributedText text={subtitle} id={data.id} />
        </span>
      </span>
    </Clickable>
  );
};

export default BlockButtonItem;
